import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { ABTicketManager } from '../lib/ab/ticket-manager';
import { ABObjectCount } from '../lib/ab/widgets-admin';
import { CBookingListView, cBookingsBySession, cBookingSendTickets, cBookingsGroup, cBookingsUnGroup } from '../lib/mdl/c-booking-admin';
import { cEventProducerEmailsOfKind, CSessionEventView, cSessionGetEventView, cSessionSalesEnd, cSessionSalesEnded, cSessionSendBookingList } from '../lib/mdl/c-event-admin';
import { c_producers } from '../lib/mdl/c-producer';
import { sUpload } from '../lib/mdl/s-upload';
import { TPdfRecognitionType, TPdfRecognizeResponse } from '../lib/mdl/t-pdf';
import { TTicket } from '../lib/mdl/t-ticket';
import { the_alt_token } from '../lib/me/login';
import { $color, $colors } from '../lib/ui/color';
import { UIDataAdapter } from '../lib/ui/component/adapter';
import { UIButton } from '../lib/ui/component/button';
import { UIDateTimeInput } from '../lib/ui/component/date';
import { UIDialog, UIDocumentDialog } from '../lib/ui/component/dialog';
import { UITextInput } from '../lib/ui/component/input';
import { UIAutofill } from '../lib/ui/component/input-base';
import { UIList } from '../lib/ui/component/list';
import { UITable, UITableDefinition, UITableRow } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox, UICheckboxRadioOptions, UIExclusiveToggles, UIRadio } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { UIContent, UIContentPreview, UIDragDelegate, UIDropDelegate, uiInitDropTarget, uiStartDrag } from '../lib/ui/dnd';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { onInteract } from '../lib/ui/touch';
import { UIRelPos } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { $log } from '../lib/y/debug';
import { $on, $trigger, YEventHandlers } from '../lib/y/events';
import { $fn } from '../lib/y/fn';
import { $form } from '../lib/y/form';
import { yGetI18N } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { $time } from '../lib/y/time';
import { YId } from '../lib/y/types';
import { AdminPdfButton } from './pdf';
import { AdminSendMessageDialog } from './templates';

declare module '../lib/ui/dnd' {
    export interface UIContentTypes {
        'ticket': [TTicket, TTicket];
    }
}

class ScheduleDialog extends UIDialog {
    constructor(manager: ABTicketManager, session: CSessionEventView) {
        super({ close: true, classes: 'm-500' });

        const when = new UIDateTimeInput({ placeholder: 'Time', required: true });
        when.value = $time(session.time).add(-session.sales_end, $time.hour);

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Schedule assign+send'),
            when,
            $el.DIV('l-button-row', new UIButton({
                title: "Schedule", classes: 'm-round m-compact', on_click: () => {
                    if(!$form.validate(when)) return;
                    return manager.schedule(when.value).then(() => {
                        this.dismiss(true);
                    }, uiToast);
                }
            }))
        ]));
    }
}

class UploadTicketsDialog extends UIDialog<TPdfRecognizeResponse> {
    constructor(session: CSessionEventView) {
        super({ close: true, classes: 'm-500' });

        const sales_ended = cSessionSalesEnded(session);
        const content = this.content;

        const option_values: UICheckboxRadioOptions<TPdfRecognitionType>[] = [{ key: 'regex', title: 'Regex' }, { key: 'gemini', title: 'Gemini 🧪' }, { key: 'unnumbered', title: 'Unnumbered ☠️' }];
        const options = new UIExclusiveToggles(option_values, o => new UIRadio(o));
        options.value = 'regex';
        $on(options, 'value', v => { button.method = v! });
        const assign = new UICheckbox({ title: 'Assign tickets' });
        $on(assign, 'value', v => { button.assign = v });

        const button = new AdminPdfButton(options.value, session.id, assign.value);
        button.on_response = r => {
            this.dismiss(r);
        }

        content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Upload tickets'),
            button,
            options,
            sales_ended ? assign : $el.P('small', "Tickets will be assigned and sent when sales end")
        ]));
    }
}

interface BookingGroup {
    bookings: CBookingListView[];
    amount: number;
    sort_name: string;
    color: string;
}

class TicketItem extends UIView implements UIDragDelegate {
    private _ticket: TTicket;
    private _box: TicketsBox;

    constructor(ticket: TTicket, manager: ABTicketManager, box: TicketsBox) {
        const remove = $el.ICON(UIIcon.TRASH_CAN);

        const rows = $el.DIV('r-rows', ticket.seat ? [
            $el.DIV('tl-box-item-row t-w-bold', ticket.seat.section),
            $el.DIV('tl-box-item-row', ['Row: ', $el.SPAN('t-w-bold', '' + ticket.seat.row), ' Seat: ', $el.SPAN('t-w-bold', '' + ticket.seat.seat)])
        ] : [
            $el.DIV('tl-box-item-row', '-'),
            $el.DIV('tl-box-item-row', ticket.name),
        ]);

        super('div', 'tl-box-item u-clickable', [
            rows,
            remove
        ]);

        rows.addEventListener('click', () => new UIDocumentDialog(`/docs/tickets/${ticket.id}?token=${the_alt_token}&inline=true`, { page_title: ticket.name }).present());

        this._ticket = ticket;
        this._box = box;

        remove.addEventListener('click', () => {
            if(box.uploading) return;
            box.uploading = true;
            manager.remove(ticket.id).then(() => {
                box.uploading = false;
                box.reload();
                $trigger(box, 'change');
            }, () => {
                box.uploading = false;
            });
        });

        onInteract(this, e => uiStartDrag(e, this));
    }

    dragStart(): [UIContent, UIView] {
        $log('app', 'drag started!!!');
        return [{ type: 'ticket', value: this._ticket }, this];
    }

    dragDrop(): boolean | void {
        $log('app', 'drag drop!!!', this._box);
        this._box.reload();
        return false;
    }

    get ticket() {
        return this._ticket;
    }
}

class TicketsBox extends UIView implements UIDropDelegate {
    $handlers: YEventHandlers<{ change: void }>;

    private _booking?: CBookingListView;
    private _booking_id?: YId;
    private _uploading?: boolean;
    private _adapter: UIDataAdapter<TTicket>;
    private _manager: ABTicketManager;
    private _count_span?: HTMLElement;

    constructor(manager: ABTicketManager, row?: UITableRow<CBookingListView>, count_span?: HTMLElement) {
        super('div', 'tl-tickets-box');
        this._count_span = count_span;
        uiInitDropTarget(row ?? this, this);

        this.ui_add(new UIList(this._adapter = new UIDataAdapter<TTicket>(ticket => new TicketItem(ticket, manager, this)), { classes: 'l-v r-tickets' }));
        // const link = $el.A('/docs/tickets/'+ticket.id+'?token='+$tl.the_login!.alt_token, ticket.name);
        // return $el.DIV('r-ticket', [ link, remove ]);

        this._booking_id = row?.item.id;
        this._manager = manager;

        if(row) {
            const booking = row.item;
            this._booking = booking;

            const send = $el.ICON(UIIcon.PAPER_PLANE);
            this.ui_add(send);
            send.addEventListener('click', () => {
                this.uploading = true;
                cBookingSendTickets(booking.id).then(b => {
                    Object.assign(booking, b)
                    this.uploading = false;
                    this.reload();
                }, () => {
                    this.uploading = false;
                });
            });
        } else {
            this.classList.add('m-unassigned');
        }

        this.reload();
    }

    dropEnter(content: UIContentPreview) {
        return content.type == 'files' || content.type == 'ticket' && content.value.booking_id != this._booking_id;
    }

    drop(content: UIContent) {
        if(this._uploading) return;

        if(content.type === 'files') {
            this.uploading = true;
            const files = content.value as File[];

            const promises = files.map(f => sUpload(f, 'ticket', undefined, 0).then(response => {
                this._manager.create(this._booking_id, response.id, f.name).then(() => {
                    this.reload();
                    $trigger(this, 'change');
                }, error => {
                    $log('app', 'error', error);
                });
            }, response => {
                $log('app', 'error response', response);
            }));

            return Promise.all(promises).then(() => {
                this.uploading = false;
                return true;
            });
        } else if(content.type === 'ticket') {
            return this._manager.assign(content.value.id, this._booking_id).then(() => {
                $log('app', 'assigned ticket', content.value.id, content.value.name, this._booking_id);
                this.reload();
                $trigger(this, 'change');
                return true;
            });
        }

        return false;
    }

    set uploading(uploading: boolean) {
        this.classList.toggle('f-uploading', uploading);
        this._uploading = uploading;
    }

    reload() {
        const tickets = this._manager.forBooking(this._booking_id);
        this._adapter.data = tickets;
        this.classList.toggle('f-has-tickets', tickets.length != 0);
        this.classList.toggle('f-tickets-sent', !!this._booking?.is_tickets_sent);

        if(this._count_span)
            this._count_span.textContent = tickets.length ? `(${tickets.length})\u00a0` : '';
    }
}

export class AdminEventBookingsScreen extends UIScreen {
    constructor(private _session_id: YId) {
        super();
        this.classList.add('m-right-sidebar');
        this.title = 'Booking list';
    }

    protected override async fillContent(content: UIViewSpec) {
        const total_ticket_count = $el.SPAN();
        const unassigned_ticket_count = $el.SPAN();

        const session = await cSessionGetEventView(this._session_id);
        const producer = c_producers.maybe(session.event.producer_id)!;
        const response = await cBookingsBySession(session.id);

        let groups: { [id: YId]: BookingGroup } = {};

        const manager = new ABTicketManager(session, response.tickets, total_ticket_count);

        const doSearch = () => {
            source.filter = filter_bar.search_input!.getFilter((b, w) => {
                if(b.user_email.toLowerCase().includes(w)) return true;
                if((b.user_first_name + ' ' + b.user_last_name).toLowerCase().includes(w)) return true;
                return false;
            });
        };

        //Top email bar
        const booking_list_email = new UITextInput({ autofill: UIAutofill.EMAIL, kind: 'k-line', required: true });
        const emails = cEventProducerEmailsOfKind(session.event, 'booking_list');
        $log('app', 'got emails', emails);
        if(emails.length) {
            booking_list_email.value = emails[0];
        } else {
            booking_list_email.value = producer.email ?? '';
        }
        const send_booking_list = new UIButton({
            icon: UIIcon.PAPER_PLANE, on_click: () => {
                if(!$form.validate(booking_list_email)) return;
                return cSessionSendBookingList(session.id, booking_list_email.value).then(() => {
                    uiToast('Booking list sent');
                }, uiToast);
            }
        });

        const booking_count = new ABObjectCount('bookings');
        const ticket_count = new ABObjectCount('tickets');

        const group_bookings = new UIButton({
            kind: 'k-filter', icon: UIIcon.LINK, title: 'Group', on_click: () => {
                return cBookingsGroup(source.checked_models).then(() => {
                    reloadSource();
                    source.checkAll(false);
                    return true;
                });
            }
        });
        group_bookings.ui_hide();

        const ungroup_bookings = new UIButton({
            kind: 'k-filter', icon: UIIcon.LINK, title: 'Ungroup', classes: 'm-inline', on_click: () => {
                return cBookingsUnGroup(source.checked_models).then(() => {
                    reloadSource();
                    source.checkAll(false);
                    return true;
                });
            }
        });
        ungroup_bookings.ui_hide();

        const filter_bar = new ABFilterBar({
            on_search: doSearch,
            left_content: [
                group_bookings,
                ungroup_bookings,
            ],
            right_content: [
                new UIButton({ kind: 'k-filter', icon: UIIcon.ARROW_UP_RIGHT_FROM_SQUARE, title: 'Booking list', link: `https://producer.abundolive.se/booking_list/${response.booking_list_token}` }),
                new UIButton({ kind: 'k-filter', icon: UIIcon.DOWNLOAD, title: 'Excel', link: `/docs/booking_list/${response.booking_list_token}/excel` }),
                new UIButton({ kind: 'k-filter', icon: UIIcon.DOWNLOAD, title: 'PDF', link: `/docs/session/${session.id}/bookings?format=pdf&token=${the_alt_token}` }),
                new UIButton({
                    kind: 'k-filter', icon: UIIcon.MESSAGE, title: 'Message all', on_click: () => {
                        new AdminSendMessageDialog(undefined, session.id).present();
                    }
                }),
                booking_count, ticket_count],
            no_header: true
        });

        const upload_tickets = new UIButton({
            kind: 'k-text', title: 'Upload tickets', classes: 'm-inline', on_click: () => {
                new UploadTicketsDialog(session).present().then(result => {
                    manager.tickets = result.tickets;
                    manager.scheduled = result.scheduled;
                    reloadSource();
                    updateSchedule();
                    unassigned_tickets.reload();
                });
            }
        });

        const send_tickets = new UIButton({
            kind: 'k-text', title: 'Send all', classes: 'm-inline', on_click: () => {
                return manager.sendAll().then(() => {
                    reloadSource();
                    return true;
                });
            }
        });

        const remove_all_tickets = new UIButton({
            kind: 'k-text', title: 'Remove all tickets', classes: 'm-inline', on_click: () => {
                return manager.removeAll().then(() => {
                    reloadSource();
                    unassigned_tickets.reload();
                    return true;
                })
            }
        });

        const assign_tickets = new UIButton({
            kind: 'k-text', title: 'Assign tickets', classes: 'm-inline', on_click: () => {
                return manager.assignAll().then(() => {
                    reloadSource();
                    unassigned_tickets.reload();
                    return true;
                })
            }
        });

        const schedule_tickets = new UIButton({
            kind: 'k-text', title: 'Schedule tickets', classes: 'm-inline', on_click: () => {
                new ScheduleDialog(manager, session).present().then(() => {
                    updateSchedule();
                });
            }
        });

        const unschedule_tickets = new UIButton({
            kind: 'k-text', title: 'Unschedule tickets', classes: 'm-inline', on_click: () => {
                return manager.schedule().then(() => {
                    updateSchedule();
                    return true;
                })
            }
        });

        const scheduled_text = $el.P('text');

        let duplicate_warning: HTMLElement | undefined;

        const def = new UITableDefinition<CBookingListView>();
        def.addCustom({
            width: -4, classes: 'm-no-padding m-no-title-padding', decorator: (td, row) => {
                const m = row.item;
                if(m.group_id)
                    td.style.background = groups[m.group_id].color;
            }
        })
        def.addCheckbox();
        def.addValue(e => e.user_first_name + ' ' + e.user_last_name, {
            title: 'Name', width: 160, sort: b => {
                return b.group_id ? groups[b.group_id].sort_name + b.user_first_name + ' ' + b.user_last_name : b.user_first_name + ' ' + b.user_last_name;
            }, on_click: row => {
                $location.push('user', { user_id: row.item.user_id });
            }, classes: 'u-clickable'
        });
        def.addValue(e => e.quantity_booked, $fmt.NUMBER, { title: 'Amount', width: -90 });
        def.addValue(e => e.number, { title: 'Number', width: -100 });
        def.addCustom({
            title: 'Tickets', width: -250, decorator: (td, row) => {
                td.classList.add('m-no-padding');
                const box = new TicketsBox(manager, row);
                $on(box, 'change', () => { checkTickets(source.data) });
                td.ui_add(box);
            }, comparator: (a, b, o) => {
                const a_name = manager.nameForBooking(a.id);
                const b_name = manager.nameForBooking(b.id);
                return a_name < b_name ? -o : a_name > b_name ? o : 0;
            }
        });

        def.preference_prefix = 'event_bookings';

        const source = new YArrayDataSource<CBookingListView>();

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                $el.DIV('r-top-line b', [
                    $el.DIV('l-v', [
                        $el.I(yGetI18N(session.event.name) + ' - ' + $time(session.time).format("yyyy-MM-dd HH:mm")),
                        $el.I('Sales end: ' + cSessionSalesEnd(session).format("yyyy-MM-dd HH:mm"))
                    ]),
                    $el.S(),
                    $el.DIV('r-send', ['Send booking list to', booking_list_email, send_booking_list])
                ]),
                filter_bar,
                new UITable(def, source, { progressive: false }),
            ]))
        ]);

        let current_message: string | undefined;

        const sidebar_header = $el.DIV('tl-sidebar-title', ['Tickets\u00a0', total_ticket_count]);

        const checkTickets = (bookings: readonly CBookingListView[]) => {
            let new_message: string | undefined;

            const map: { [key: string]: string } = {};
            for(const b of bookings) {
                const tickets = manager.forBooking(b.id)
                for(const t of tickets) {
                    if(map[t.upload_id + '' + t.code]) {
                        new_message = map[t.upload_id + '' + t.code] + ' and ' + b.user_first_name + ' ' + b.user_last_name + ' have duplicate tickets';
                        break;
                    }
                    map[t.upload_id + '' + t.code] = b.user_first_name + ' ' + b.user_last_name;
                }

                if(new_message) {
                    break;
                } else if(tickets.length < b.quantity_booked) {
                    new_message = b.user_first_name + ' ' + b.user_last_name + ' does not have tickets';
                    break;
                } else if(tickets.length > b.quantity_booked) {
                    new_message = b.user_first_name + ' ' + b.user_last_name + ' has too many tickets';
                    break;
                }
            }

            if(new_message !== current_message) {
                if(duplicate_warning) {
                    duplicate_warning.remove();
                    duplicate_warning = undefined;
                }

                if(new_message) {
                    duplicate_warning = $el.ICON(UIIcon.TRIANGLE_EXCLAMATION | UIIcon.WEIGHT_SOLID, 'warning'),
                        addTooltip(new_message, duplicate_warning, { hover: duplicate_warning, position: UIRelPos.Below, offset: 12 })
                    sidebar_header.ui_add(duplicate_warning);
                }

                current_message = new_message;
            }
        }

        const calcGroups = (bookings: readonly CBookingListView[]) => {
            let n_groups = 0;
            groups = {};
            for(const b of bookings) {
                const g = b.group_id;
                if(g) {
                    const group = groups[g];
                    if(group) {
                        group.bookings.push(b);
                        group.amount += b.quantity_booked;
                        if($fn.ICOMPARE(b.user_first_name + ' ' + b.user_last_name, group.sort_name) < 0)
                            group.sort_name = b.user_first_name + ' ' + b.user_last_name
                    } else {
                        groups[g] = {
                            bookings: [b], amount: b.quantity_booked, sort_name: b.user_first_name + ' ' + b.user_last_name,
                            color: $color.HEX($color.make($colors.google[(n_groups * 3 + 1) % $colors.google.length][5]))
                        };
                        n_groups++;
                    }
                }
            }

            $log('app', 'groups', groups);
        }

        const setSourceData = (bookings: CBookingListView[]) => {
            bookings = bookings.filter(b => b.quantity_booked > 0 && !b.is_test);
            calcGroups(bookings);
            checkTickets(bookings);
            source.data = bookings;
            booking_count.number = bookings.length;
            ticket_count.number = bookings.reduce((n, b) => n + b.quantity_booked, 0);
        }

        const reloadSource = () => {
            calcGroups(source.data);
            checkTickets(source.data);
            source.reload();
        }

        const updateSchedule = () => {
            schedule_tickets.ui_show(undefined, !manager.scheduled);
            unschedule_tickets.ui_show(undefined, !!manager.scheduled);
            scheduled_text.textContent = manager.scheduled ? 'Scheduled for ' + manager.scheduled.format('yyyy-MM-dd HH:mm') : '';
        }

        $on(source, 'checked', () => {
            const checked_models = source.checked_models;
            $log('app', 'checked', checked_models);
            group_bookings.ui_show(undefined, checked_models.length > 1);
            ungroup_bookings.ui_show(undefined, checked_models.some(b => !!b.group_id))
        });

        const unassigned_tickets = new TicketsBox(manager, undefined, unassigned_ticket_count);

        const sidebar = this.addSidebar(UIRelPos.Right, 300, 'tl-new-sidebar');
        sidebar.ui_add([
            sidebar_header,
            upload_tickets,
            send_tickets,
            remove_all_tickets,
            assign_tickets,
            schedule_tickets,
            unschedule_tickets,
            scheduled_text,
            $el.DIV('tl-sidebar-heading', ['Unassigned tickets\u00a0', unassigned_ticket_count, new UIButton({ icon: UIIcon.DOWNLOAD, link: `/docs/tickets/unassigned/by_session/${session.id}?token=${the_alt_token}` })]),
            unassigned_tickets
        ]);

        content.ui_add(sidebar);

        setSourceData(response.bookings);
        updateSchedule();
    }
}
